import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, MapPin, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanyCard } from "@/components/CompanyCard";
import { Spinner, EmptyState } from "@/components/shared";

const LOCATIONS = ["Dubai Marina", "Downtown Dubai", "Dubai Creek Harbour", "Business Bay", "Palm Jumeirah", "Dubai Hills Estate", "Arabian Ranches"];

export default function CompaniesPage() {
  const [sp, setSp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState(sp.get("q") || "");

  const service = sp.get("service") || "";
  const location = sp.get("location") || "";
  const sort = sp.get("sort") || "relevance";
  const page = parseInt(sp.get("page") || "1");

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams();
    if (sp.get("q")) params.set("q", sp.get("q"));
    if (service) params.set("service", service);
    if (location) params.set("location", location);
    params.set("sort", sort);
    params.set("page", page);
    api.get(`/companies?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [sp, service, location, sort, page]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    next.set("page", "1");
    setSp(next);
  };

  const submitSearch = (e) => { e.preventDefault(); update("q", q); };

  return (
    <>
      <div className="propora-container py-10" data-testid="companies-page">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#007F83] mb-2">Directory</p>
        <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Find a company</h1>
        <p className="text-[#4A5568] mt-2 max-w-2xl">Explore listed companies offering property services in Dubai. Every company shown has had its trade licence checked before going live.</p>

        <form onSubmit={submitSearch} className="mt-6 bg-white rounded-2xl border border-[#E3E8E8] p-3 flex flex-col md:flex-row gap-3 shadow-soft">
          <div className="flex-1 flex items-center gap-2 px-3">
            <Search size={18} className="text-[#007F83]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword or service" data-testid="company-search-input" className="border-0 shadow-none focus-visible:ring-0 px-0" />
          </div>
          <Select value={service || "all"} onValueChange={(v) => update("service", v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-52" data-testid="filter-service"><SelectValue placeholder="All services" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All services</SelectItem>
              {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={location || "all"} onValueChange={(v) => update("location", v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-48" data-testid="filter-location"><SelectValue placeholder="All areas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" className="rounded-xl bg-[#007F83] hover:bg-[#006B70] text-white px-6" data-testid="company-search-btn">Search</Button>
        </form>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-[#4A5568]" data-testid="results-count">{data ? `${data.total} companies` : "Loading..."}</p>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-[#4A5568]" />
            <Select value={sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger className="w-44 h-9" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="review">Review score</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5">
          {!data ? <Spinner /> : data.items.length === 0 ? (
            <EmptyState title="No companies match your filters" subtitle="Try broadening your search or clearing filters.">
              <Button variant="outline" onClick={() => setSp(new URLSearchParams())} className="rounded-full">Clear filters</Button>
            </EmptyState>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {data.items.map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
          )}
        </div>

        {data && data.total > data.limit && (
          <div className="mt-8 flex justify-center gap-2" data-testid="pagination">
            <Button variant="outline" disabled={page <= 1} onClick={() => update("page", String(page - 1))} className="rounded-full">Previous</Button>
            <span className="px-4 py-2 text-sm text-[#4A5568]">Page {page} of {Math.ceil(data.total / data.limit)}</span>
            <Button variant="outline" disabled={page >= Math.ceil(data.total / data.limit)} onClick={() => update("page", String(page + 1))} className="rounded-full">Next</Button>
          </div>
        )}
      </div>
    </>
  );
}
